import { useCallback, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import { toast } from 'react-toastify';
import type { RootState } from '@/app/store/index';
import { completeQuote } from '../services/quotes';
import { useQuote } from '../context/QuoteContext';
import { toSavedQuote } from '../services/quoteSnapshot';
import { QuoteConflictError, RetiredSaveError, toQuoteSaveError } from '../services/quoteSaveErrors';
import type { QuoteSummary, CompleteQuoteRequest, QuoteError } from '../types/quote.types';
import { DEALER_PRODUCT_NOT_AUTHORISED } from '../services/quoteErrorMessage';
import { useAppDispatch } from '@/app/store/hooks';
import { quoteSaved, setCurrentStep } from '../store/quoteSlice';
import { isApiError, getFieldErrors } from '@/services/api';
import { stepForCompletionError } from '../utils/completionErrors';
import { priceChangedBlock } from '../utils/pricingErrors';
import type { BasketPricingInput, PricedBasket } from '../types/basketPricing.types';
import { basketPricingInputFrom } from '../utils/pricedLines';
import { useQuoteStepOrder } from './useQuoteStepOrder';

export interface UseCompleteSaleOptions {
    /** Called instead of the routing/error path when completion is refused with `price_changed`. */
    onPriceChanged?: (fresh: PricedBasket, refusedInput: BasketPricingInput | null) => void;
    /** Opens the shared save-error modal when completion is refused with `dealer_product_not_authorised`. */
    openErrorModal?: (error: QuoteError) => void;
}

export const useCompleteSale = ({ onPriceChanged, openErrorModal }: UseCompleteSaleOptions = {}) => {
    const dispatch = useAppDispatch();
    // #856 (Epic 7.1.8 follow-up). The LIVE, per-tenant step order — resolved
    // the same way `QuoteContext` resolves `stepOrder`, not a re-declared
    // copy. Called directly rather than threaded in from a caller so
    // `QuoteWizard`/`QuoteSummaryStep` don't need to change their call site.
    // `settled` is not gated on here: by the time a completion 422 can fire,
    // the user is on the summary step, well after `QuoteContext`'s own
    // `useQuoteStepOrder()` call (mounted with the journey) has already
    // resolved the shared `dealerConfig` Redux state this hook reads too; a
    // provisional read here would fall back to `DEALER_FIRST_ORDER`, which
    // is the universal behaviour this fix replaces, not a regression.
    const { order } = useQuoteStepOrder();
    const store = useStore<RootState>();
    const { saveLane } = useQuote();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const latestOperation = useRef(0);

    const readyToComplete = async (request: CompleteQuoteRequest) => {
        const { draftId } = store.getState().quote;
        if (saveLane.isUnsavedNow()) {
            await saveLane.flush({ quiet: true });
        } else {
            await saveLane.settled();
        }
        const { quote } = store.getState();
        if (quote.draftId !== draftId || quote.quoteId !== request.quoteId || saveLane.isUnsavedNow()) {
            throw new Error('The quote has unsaved changes, so it was not completed.');
        }
        return { draftId, version: quote.version };
    };

    const completeSale = async (request: CompleteQuoteRequest): Promise<QuoteSummary> => {
        const { draftId, version } = await readyToComplete(request);
        const releaseSaves = saveLane.holdSaves();
        const isRetired = () => {
            const { quote } = store.getState();
            return quote.draftId !== draftId || quote.quoteId !== request.quoteId;
        };
        if (isRetired()) {
            releaseSaves();
            throw new RetiredSaveError();
        }
        const operation = ++latestOperation.current;
        const pricingInput = basketPricingInputFrom(store.getState().quote);
        setIsLoading(true);
        setError(null);
        const loadingToastId = toast.loading('Completing sale...', {
            isLoading: true,
            autoClose: false,
            position: 'bottom-right',
        });

        try {
            const response = await completeQuote(request.quoteId, version);
            if (isRetired()) {
                toast.dismiss(loadingToastId);
                throw new RetiredSaveError();
            }
            toast.update(loadingToastId, {
                render: 'Quote completed successfully!',
                type: 'success',
                isLoading: false,
                autoClose: 3000,
            });

            const saved = toSavedQuote(response);
            dispatch(quoteSaved({ draftId, quoteId: saved.id, quoteRef: saved.quoteRef, status: saved.status, version: saved.version }));

            return response;
        } catch (err) {
            if (err instanceof RetiredSaveError) throw err;
            if (isRetired()) {
                toast.dismiss(loadingToastId);
                throw new RetiredSaveError();
            }
            const mapped = toQuoteSaveError(err);
            if (mapped instanceof QuoteConflictError) {
                toast.dismiss(loadingToastId);
                saveLane.reportConflict(mapped);
                throw err;
            }

            const fresh = priceChangedBlock(err);
            if (fresh && onPriceChanged) {
                toast.dismiss(loadingToastId);
                onPriceChanged(fresh, pricingInput);
                throw err;
            }

            let errorMessage = 'An unexpected error occurred';
            if (isApiError(err)) {
                errorMessage = err.message;
            } else {
                errorMessage = 'Failed to complete sale';
            }
            if (isApiError(err) && err.status === 403 && err.code === DEALER_PRODUCT_NOT_AUTHORISED && openErrorModal) {
                openErrorModal({ message: err.message, status: err.status, code: err.code, data: err.data });
                toast.dismiss(loadingToastId);
            } else if (isApiError(err) && err.status === 403) {
                // Any 403 reaching here shows the inline canonical copy, never the backend
                // string such as "Unauthorized"; the interceptor toasts all but dealer/product.
                setError("You don't have permission to perform this action.");
                toast.dismiss(loadingToastId);
            } else {
                setError(errorMessage);
                toast.update(loadingToastId, {
                    render: errorMessage,
                    type: 'error',
                    isLoading: false,
                    autoClose: 4000,
                });
            }

            // #856. The server refused to publish this quote (QuotePublicationGuard) —
            // route the user to the step that owns the failing field rather than leaving
            // them on `summary` with only a toast. dispatch(setCurrentStep(...)) directly,
            // NOT quote.goToStep(...): goToStep is refused by canProceedToStep whenever an
            // earlier step's Redux gate disagrees with the stored document the server has
            // just refused (see docs/plans/2026-08-10-quote-completion-vehicle-revalidation.md
            // D7 decision 2 / R5). getFieldErrors is the repo's single sanctioned reader of
            // the wire error shape; it returns {} for the two hand-built dead/already-
            // completed 422s (no `errors` key), so this branch is inert for those.
            // `order` is passed straight through to stepForCompletionError: routing on the
            // wrong tenant's step order would land the user on a step later in their own
            // journey than the one that actually failed (Epic 7.1.8 dealer-deferred tenants).
            if (isApiError(err) && err.status === 422) {
                const step = stepForCompletionError(getFieldErrors(err), order);
                if (step) {
                    dispatch(setCurrentStep(step));
                }
            }

            throw err;
        } finally {
            releaseSaves();
            if (latestOperation.current === operation) setIsLoading(false);
        }
    };

    // #876/D6. Additive: `quote.reset()` (QuoteContext.tsx) is Redux-only and does
    // not touch this hook's local `error` state, so `QuoteWizard`'s "Start Over"
    // handler needs an explicit way to clear a completion error that would
    // otherwise outlive the reset. Stable identity (empty dep array) because
    // `setError`'s own identity is stable and `onReset` feeds a component
    // (QuoteProgressNav) rendered twice.
    const clearError = useCallback(() => setError(null), []);

    return {
        completeSale,
        isLoading,
        error,
        clearError,
    };
};
